<template>
    <div class="shipment-page">
        <div class="table-container">
            <!-- 标题区域 -->
            <div class="page-header">
                <div class="header-bar"></div>
                <div class="header-content">
                    <h2 class="header-title">发货申请单审核</h2>
                    <div class="header-sub">SHIPPING REQUEST APPROVAL</div>
                </div>
                <div class="header-meta">
                    <span class="meta-label">申请日期</span>
                    <span class="meta-value">{{ formData.createTime || '—' }}</span>
                </div>
            </div>

            <table class="table-layout">
                <tbody>
                    <!-- 第 1 行：基础信息 -->
                    <tr>
                        <td class="table-cell label-cell">公司名称</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.companyName }}</span>
                        </td>
                        <td class="table-cell label-cell">业务员</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.salesMan }}</span>
                        </td>
                    </tr>

                    <!-- 第 2 行：申请信息 -->
                    <tr>
                        <td class="table-cell label-cell">申请原因</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.applyCause }}</span>
                        </td>
                        <td class="table-cell label-cell">使用设备及描述</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.useDevice }}</span>
                        </td>
                    </tr>

                    <!-- 申请内容标题 -->
                    <tr class="section-row">
                        <td class="section-title" colspan="8">
                            <i class="el-icon-document section-icon"></i>
                            <span>申请内容</span>
                        </td>
                    </tr>

                    <!-- 申请内容表头 -->
                    <tr class="content-head-row">
                        <td colspan="7" class="content-td-wrapper">
                            <el-table
                                :data="formData.contentList || []"
                                border
                                size="small"
                                class="content-el-table"
                                :header-cell-style="{ background: '#f5f7fa', color: '#606266', textAlign: 'center' }"
                                :cell-style="{ textAlign: 'center' }">
                                <el-table-column label="运营商" min-width="100">
                                    <template slot-scope="scope">
                                        {{ getOperatorLabel(scope.row.operatorName) }}
                                    </template>
                                </el-table-column>
                                <el-table-column label="卡类型" min-width="180">
                                    <template slot-scope="scope">
                                        {{ (scope.row.cardList || []).filter(t => t != null && t != undefined && t != '').join('，') }}
                                    </template>
                                </el-table-column>
                                <el-table-column label="套餐内容(注明配套餐出货或用户充值)/地区" min-width="200" show-overflow-tooltip>
                                    <template slot-scope="scope">
                                        {{ scope.row.packageContent }}
                                    </template>
                                </el-table-column>
                                <el-table-column label="数量(PCS)" prop="quantity" min-width="90"></el-table-column>
                                <el-table-column label="单价(元)" prop="unitPrice" min-width="90"></el-table-column>
                                <el-table-column label="总金额(元)" prop="grossAmount" min-width="100"></el-table-column>
                            </el-table>
                        </td>
                    </tr>

                    <!-- 运输 & 发货日期 -->
                    <tr>
                        <td class="table-cell label-cell">运输方式</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ transportLabel }}</span>
                        </td>
                        <td class="table-cell label-cell">要求发货日期</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.deliveryDate }}</span>
                        </td>
                    </tr>

                    <!-- 收货地址 -->
                    <tr>
                        <td class="table-cell label-cell">收货地址</td>
                        <td class="table-cell" colspan="7">
                            <span class="cell-text">{{ formData.shippingAddress }}</span>
                        </td>
                    </tr>

                    <!-- 收货人信息 -->
                    <tr>
                        <td class="table-cell label-cell">收货人</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.consignee }}</span>
                        </td>
                        <td class="table-cell label-cell">收货人电话</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ formData.receivingPhone }}</span>
                        </td>
                    </tr>

                    <!-- 付款信息 -->
                    <tr>
                        <td class="table-cell label-cell">是否付款</td>
                        <td class="table-cell" colspan="3">
                            <div class="pay-block">
                                <span class="cell-text pay-status" :class="{ 'pay-yes': formData.whetherToPay == '1', 'pay-no': formData.whetherToPay == '0' }">
                                    {{ formData.whetherToPay == '1' ? '是' : formData.whetherToPay == '0' ? '否' : '—' }}
                                </span>
                                <span class="cell-text pay-date-text" v-if="formData.toPayDate">
                                    <i class="el-icon-date"></i> {{ formData.toPayDate }}
                                </span>
                            </div>
                        </td>
                        <td class="table-cell label-cell">付款方式</td>
                        <td class="table-cell" colspan="3">
                            <span class="cell-text">{{ payModeLabel }}</span>
                        </td>
                    </tr>

                    <!-- 支付凭证 -->
                    <tr>
                        <td class="table-cell label-cell">支付凭证 / 申请说明</td>
                        <td class="table-cell" colspan="7">
                            <div class="upload-block">
                                <div v-if="payVoucherList.length > 0" class="image-list">
                                    <el-image
                                        v-for="(img, idx) in payVoucherList"
                                        :key="idx"
                                        class="voucher-image"
                                        :src="img"
                                        :preview-src-list="payVoucherList"
                                        fit="cover">
                                    </el-image>
                                </div>
                                <span v-else class="cell-text cell-empty">暂无支付凭证</span>
                            </div>
                        </td>
                    </tr>

                    <!-- 备注 -->
                    <tr>
                        <td class="table-cell label-cell">备注</td>
                        <td class="table-cell" colspan="7">
                            <span class="cell-text">{{ formData.remark || '—' }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 流程图 -->
        <div class="table-container flow-container">
            <div class="page-header flow-header">
                <div class="header-bar"></div>
                <div class="header-content">
                    <h2 class="header-title">流程图</h2>
                    <div class="header-sub">FLOW DIAGRAM</div>
                </div>
            </div>
            <div class="flow-diagram-wrap">
                <p v-html="str" class="diagram-content"></p>
            </div>
        </div>

        <!-- 流程历史跟踪 -->
        <div class="table-container flow-container">
            <div class="page-header flow-header">
                <div class="header-bar"></div>
                <div class="header-content">
                    <h2 class="header-title">流程历史跟踪</h2>
                    <div class="header-sub">PROCESS HISTORY</div>
                </div>
            </div>
            <div class="flow-table-wrap">
                <el-table :data="tableData" border>
                    <el-table-column prop="ids" label="序号" align="center" width="60px"></el-table-column>
                    <el-table-column prop="activityName" label="名称" align="center"></el-table-column>
                    <el-table-column prop="startTime" label="开始时间" align="center"></el-table-column>
                    <el-table-column prop="endTime" label="结束时间" align="center"></el-table-column>
                    <el-table-column prop="assignee" label="负责人" align="center"></el-table-column>
                    <el-table-column prop="status" label="处理结果" align="center"></el-table-column>
                    <el-table-column prop="reason" label="处理意见" align="center"></el-table-column>
                </el-table>
            </div>
        </div>
    </div>
</template>

<script>
    import { processInstanceImage } from "@/api/api.js";
    import { historicActivityInstance } from "@/api/api.js";
    import { findShipmentRequestById, outEntrepotByShipmentId, exportOutEntrepot } from "@/api/api.js";
    export default {
        name: "shipmentFlowView",
        data() {
            return {
                processId: null,
                id: null,
                str: "",
                formData: {},
                formData2: {},
                tableData: [],
                domain: process.env.VUE_APP_BASE_URL,
            }
        },
        computed: {
            transportLabel() {
                const map = { '0': '自提', '1': '送货', '2': '快递寄付', '3': '快递到付' };
                return map[this.formData.modeOfTransport] || '—';
            },
            payModeLabel() {
                const map = { '0': '对公转账', '1': '微信', '2': '支付宝', '3': '测试' };
                return map[this.formData.payMode] || '—';
            },
            payVoucherList() {
                if(this.formData.payVoucherList && this.formData.payVoucherList.length > 0){
                    let url = this.formData.payVoucherList.map(u => this.domain + u);
                    return url;
                } else {
                    return [];
                }
            }
        },
        methods: {
            getOperatorLabel(val) {
                const map = { '0': '中国移动', '1': '中国联通', '2': '中国电信', '3': '国际' };
                return map[val] || '—';
            },
            initData() {
                if (this.processId) {
                    let params = { processInstanceId: this.processId };
                    processInstanceImage(params).then(res => {
                        this.str = res.data
                    });
                    historicActivityInstance(params).then(res => {
                        if (res.data.code == 200) {
                            this.tableData = res.data.data;
                            this.tableData.map((item, index) => {
                                item.ids = index + 1;
                            })
                        }
                    });
                }
                let params1 = { id: this.id };
                findShipmentRequestById(params1).then(res => {
                    if (res.data.code == 200) {
                        this.formData = res.data.data;
                    }
                });
                let params2 = { shipmentId: this.id };
                outEntrepotByShipmentId(params2).then(res => {
                    if (res.data.code == 200) {
                        this.formData2 = res.data.data;
                    }
                    if (!this.formData2) {
                        this.formData2 = {
                            id: null, customerName: null, billingDate: null, remark: null,
                            documenter: null, financial: null, entrepot: null, number: null,
                            customerSignature: null, total: null, status: null, shipmentId: null,
                            flowId: null,
                            contentList: [
                                { id: null, categoryName: null, quantity: null, units: null, unitPrice: null, paymentGoods: null, remark: null, outEntrepotId: null },
                                { id: null, categoryName: null, quantity: null, units: null, unitPrice: null, paymentGoods: null, remark: null, outEntrepotId: null },
                                { id: null, categoryName: null, quantity: null, units: null, unitPrice: null, paymentGoods: null, remark: null, outEntrepotId: null },
                                { id: null, categoryName: null, quantity: null, units: null, unitPrice: null, paymentGoods: null, remark: null, outEntrepotId: null }
                            ]
                        }
                    }
                })
            },
        },
        created() {
            this.processId = this.$route.query.processId;
            this.id = this.$route.query.id;
        },
        mounted() {
            this.initData()
        },
        watch: {
            '$route.query.id': {
                handler(newId, oldId) {
                    if (newId && newId !== oldId) {
                        this.id = newId;
                        this.processId = this.$route.query.processId;
                        this.formData = {};
                        this.formData2 = {};
                        this.tableData = [];
                        this.str = "";
                        this.initData();
                    }
                },
                immediate: false,
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409EFF;
    @primary-dark: #337ECC;
    @success: #67C23A;
    @danger: #F56C6C;
    @warning: #E6A23C;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @text-placeholder: #C0C4CC;
    @border-base: #DCDFE6;
    @border-light: #E4E7ED;
    @bg-page: #F0F2F5;
    @bg-section: #F5F7FA;
    @bg-header: linear-gradient(135deg, #409EFF 0%, #337ECC 100%);

    .shipment-page {
        min-height: 100vh;
        background: @bg-page;
        padding: 24px 16px 40px;
        box-sizing: border-box;
    }

    /* ================= 容器 ================= */
    .table-container {
        max-width: 1200px;
        margin: 0 auto;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        overflow: hidden;
        position: relative;
        &.flow-container {
            margin-top: 16px;
        }
    }

    /* ================= 标题区域 ================= */
    .page-header {
        display: flex;
        align-items: center;
        padding: 20px 28px;
        background: @bg-header;
        color: #fff;
        position: relative;
        &.flow-header {
            padding: 16px 28px;
            .header-title {
                font-size: 18px;
            }
        }
    }
    .header-bar {
        width: 5px;
        height: 36px;
        background: #fff;
        border-radius: 3px;
        margin-right: 14px;
        opacity: 0.85;
    }
    .header-content {
        flex: 1;
    }
    .header-title {
        margin: 0;
        font-size: 22px;
        font-weight: 600;
        letter-spacing: 2px;
        line-height: 1.2;
    }
    .header-sub {
        margin-top: 4px;
        font-size: 12px;
        letter-spacing: 1.5px;
        opacity: 0.7;
    }
    .header-meta {
        text-align: right;
        font-size: 13px;
        .meta-label {
            display: block;
            opacity: 0.7;
            font-size: 12px;
        }
        .meta-value {
            display: block;
            font-size: 16px;
            font-weight: 500;
            margin-top: 2px;
        }
    }

    /* ================= 表格 ================= */
    .table-layout {
        table-layout: auto;
        border-collapse: collapse;
        width: 100%;
        margin: 0;

        td {
            border: 1px solid @border-light;
            padding: 10px 12px;
            text-align: left;
            vertical-align: middle;
            font-size: 14px;
            color: @text-primary;
            line-height: 1.5;
        }

        .label-cell {
            background: @bg-section;
            color: @text-regular;
            font-weight: 500;
            text-align: center;
            width: 92px;
            min-width: 92px;
            max-width: 145px;
            white-space: nowrap;
            position: relative;
        }

        .section-row {
            td {
                background: #ECF5FF;
                border-color: #B3D8FF;
            }
        }
        .section-title {
            color: @primary;
            font-weight: 600;
            font-size: 14px;
            padding: 10px 16px;
            .section-icon {
                margin-right: 6px;
                font-size: 15px;
            }
        }

        .content-head {
            .th-cell {
                background: @bg-section;
                color: @text-regular;
                font-weight: 600;
                text-align: center;
                font-size: 13px;
                padding: 8px 6px;
            }
        }
        .content-row {
            td {
                transition: background 0.2s;
            }
            &:hover td {
                background: #FAFBFC;
            }
        }
        .content-td-wrapper {
            padding: 8px 16px 16px !important;
            border: none !important;
        }
        .content-el-table {
            width: 100%;
            border-radius: 4px;
            overflow: hidden;
            /deep/ .el-table__header th {
                background: #f5f7fa;
                color: #606266;
                font-weight: 600;
            }
            /deep/ .el-table__body td {
                text-align: center;
            }
        }
    }

    /* ================= 文本展示 ================= */
    .cell-text {
        color: @text-primary;
        font-size: 14px;
        &.cell-empty {
            color: @text-secondary;
        }
    }

    /* 申请内容表头单元格 */
    .th-cell, .td-cell {
        border: 1px solid @border-light;
        padding: 10px 8px;
        text-align: center;
        vertical-align: middle;
        &:nth-child(1) { min-width: 160px; }
        &:nth-child(2) { min-width: 210px; }
    }
    .action-cell {
        text-align: center;
        color: @text-secondary;
    }

    /* ================= 付款块 ================= */
    .pay-block {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    .pay-status {
        font-weight: 600;
        &.pay-yes { color: @success; }
        &.pay-no { color: @danger; }
    }
    .pay-date-text {
        color: @text-secondary;
        font-size: 13px;
        i { margin-right: 4px; }
    }

    /* ================= 图片 ================= */
    .upload-block {
        padding: 4px 0;
    }
    .image-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .voucher-image {
        width: 100px;
        height: 100px;
        border-radius: 6px;
        border: 1px solid @border-light;
    }

    /* ================= 流程图 ================= */
    .flow-diagram-wrap {
        padding: 20px;
        overflow-x: auto;
        .diagram-content {
            margin: 0;
        }
    }

    /* ================= 流程表格 ================= */
    .flow-table-wrap {
        padding: 16px;
    }
</style>
